import { TTSConfig, TTSState, TTSEvent, TTSEventHandler, TTSEventType, TTSVoice } from './types';

const DEFAULT_CONFIG: TTSConfig = {
  lang: 'zh-CN',
  rate: 1.0,
  pitch: 1.0,
  volume: 1.0,
  chunkSize: 40,  // 每块最多40字符，确保单次朗读 < 8秒
  voiceName: '',
};

export class TTSService {
  private config: TTSConfig;
  private state: TTSState;
  private eventHandlers: Map<TTSEventType, TTSEventHandler[]> = new Map();
  private stopFlag = false;

  // 全文分块
  private chunks: string[] = [];
  private fullText = '';
  private fullTextCleaned = '';

  // 当前 chunk 的句子列表（用于跟读高亮）
  private chunkSentences: { text: string; start: number }[] = [];
  private currentSentence = '';

  // 当前朗读位置
  private currentChunkIdx = 0;
  private retryCount = 0;          // 当前 chunk 重试次数
  private maxRetries = 3;          // 单块最大重试次数

  // 看门狗：检测 TTS 引擎是否卡死
  private watchdogTimer: number | null = null;
  private lastActivityTime = 0;
  private watchdogInterval = 8000; // 8秒无活动视为卡死

  // 朗读代次：每次 speak() 递增，用于使旧 utterance 的异步回调失效
  private speakGeneration = 0;
  // 当前整段朗读的完成回调（speak() 返回的 Promise 在整段朗读结束或 stop 时 resolve）
  private speakResolve: (() => void) | null = null;

  // 浏览器检测
  private speechSynthesisAvailable = false;
  private synth: SpeechSynthesis | null = null;

  constructor(config?: Partial<TTSConfig>) {
    this.config = { ...DEFAULT_CONFIG, ...config };
    this.state = {
      isSpeaking: false,
      isPaused: false,
      currentChunkIndex: 0,
      totalChunks: 0,
      currentText: '',
      progress: 0,
    };

    // 兼容 webkit 前缀
    if ('speechSynthesis' in window) {
      this.synth = window.speechSynthesis;
    } else if ('webkitSpeechSynthesis' in window) {
      this.synth = (window as any).webkitSpeechSynthesis;
    }
    this.speechSynthesisAvailable = this.synth !== null;

    console.log(`[TTS] speechSynthesis=${this.speechSynthesisAvailable}`);
  }

  // ========== 语音列表 ==========

  getVoices(): TTSVoice[] {
    if (!this.synth) return [];
    try {
      return this.synth.getVoices()
        .filter(v => v.lang.startsWith('zh') || v.lang.startsWith('en'))
        .map(v => ({
          name: v.name,
          lang: v.lang,
          localService: v.localService,
          default: v.default,
        }));
    } catch (e) {
      console.warn('[TTS] getVoices 失败:', e);
      return [];
    }
  }

  // 预热 TTS 引擎
  warmup(): void {
    if (!this.synth) return;
    try {
      this.synth.getVoices();
      const u = new SpeechSynthesisUtterance(' ');
      u.volume = 0;
      u.rate = 10;
      this.synth.speak(u);
    } catch (e) {
      console.warn('[TTS] warmup 失败:', e);
    }
  }

  // ========== 公开方法 ==========

  async speak(text: string): Promise<void> {
    if (!text || text.trim().length === 0) return;
    // 停止上一次朗读（会递增代次并 resolve 上一次的 speak Promise）
    this.stop();
    this.stopFlag = false;
    // 递增代次，使旧 utterance 的异步回调失效
    this.speakGeneration++;
    const gen = this.speakGeneration;

    this.fullText = text;
    this.fullTextCleaned = text.replace(/\s+/g, '');
    this.chunks = this.splitIntoChunks(text);
    this.currentChunkIdx = 0;
    this.retryCount = 0;

    this.state = {
      isSpeaking: true,
      isPaused: false,
      currentChunkIndex: 0,
      totalChunks: this.chunks.length,
      currentText: text,
      progress: 0,
    };

    console.log(`[TTS] 开始朗读，共 ${this.chunks.length} 块`);
    this.emit({ type: 'start', data: { text, totalChunks: this.chunks.length } });
    this.startWatchdog();
    // 返回的 Promise 在整段朗读完成（或 stop 打断）时才 resolve
    return new Promise<void>((resolve) => {
      this.speakResolve = resolve;
      this.playCurrentChunk(gen);
    });
  }

  pause(): void {
    if (this.state.isSpeaking && !this.state.isPaused && this.synth) {
      try {
        this.synth.pause();
        this.state.isPaused = true;
        this.emit({ type: 'pause' });
      } catch (e) {
        console.warn('[TTS] pause 失败:', e);
      }
    }
  }

  resume(): void {
    if (this.state.isSpeaking && this.state.isPaused && this.synth) {
      try {
        this.synth.resume();
        this.state.isPaused = false;
        this.emit({ type: 'resume' });
      } catch (e) {
        // Android 上 resume() 可能失败，重新播放当前 chunk
        console.warn('[TTS] resume 失败，重新播放:', e);
        this.state.isPaused = false;
        this.retryCount = 0;
        this.playCurrentChunk(this.speakGeneration);
      }
    }
  }

  stop(): void {
    this.stopFlag = true;
    // 递增代次，使当前正在播放的 utterance 回调失效
    this.speakGeneration++;
    this.stopWatchdog();
    try {
      if (this.synth) this.synth.cancel();
    } catch (e) { /* 忽略 */ }
    this.state = {
      isSpeaking: false,
      isPaused: false,
      currentChunkIndex: 0,
      totalChunks: 0,
      currentText: '',
      progress: 0,
    };
    this.chunkSentences = [];
    this.currentSentence = '';
    // resolve 挂起的 speak() Promise，避免调用方 await 永久挂起
    this.resolveSpeak();
    // 通知清除跟读高亮
    this.emit({
      type: 'boundary',
      data: { chunk: 0, progress: 0, sentence: null, charIndex: 0 },
    });
    this.emit({ type: 'end' });
  }

  // resolve 当前挂起的 speak() Promise（只能 resolve 一次）
  private resolveSpeak(): void {
    if (this.speakResolve) {
      const resolve = this.speakResolve;
      this.speakResolve = null;
      resolve();
    }
  }

  getState(): TTSState { return { ...this.state }; }
  updateConfig(config: Partial<TTSConfig>): void { this.config = { ...this.config, ...config }; }
  getConfig(): TTSConfig { return { ...this.config }; }

  on(eventType: TTSEventType, handler: TTSEventHandler): void {
    if (!this.eventHandlers.has(eventType)) this.eventHandlers.set(eventType, []);
    this.eventHandlers.get(eventType)!.push(handler);
  }

  off(eventType: TTSEventType, handler: TTSEventHandler): void {
    const h = this.eventHandlers.get(eventType);
    if (h) { const i = h.indexOf(handler); if (i > -1) h.splice(i, 1); }
  }

  private emit(event: TTSEvent): void {
    const h = this.eventHandlers.get(event.type);
    if (h) h.forEach(fn => fn(event));
  }

  // ========== 核心：朗读 ==========

  private findVoice(): SpeechSynthesisVoice | null {
    if (!this.config.voiceName) return null;
    try {
      return this.synth!.getVoices().find(v => v.name === this.config.voiceName) || null;
    } catch (e) {
      return null;
    }
  }

  private calcProgress(): number {
    if (!this.fullTextCleaned || this.chunks.length === 0) return 0;
    let charsBefore = 0;
    for (let i = 0; i < this.currentChunkIdx; i++) {
      charsBefore += this.chunks[i].replace(/\s+/g, '').length;
    }
    return Math.min(100, Math.round((charsBefore / this.fullTextCleaned.length) * 100));
  }

  private playCurrentChunk(gen: number): void {
    // 代次不匹配说明已被新的 speak()/stop() 取代，直接结束
    if (gen !== this.speakGeneration) {
      this.resolveSpeak();
      return;
    }
    if (this.stopFlag || this.currentChunkIdx >= this.chunks.length) {
      this.state.isSpeaking = false;
      this.stopWatchdog();
      this.resolveSpeak();
      this.emit({ type: 'end', data: { completed: !this.stopFlag } });
      return;
    }

    const chunk = this.chunks[this.currentChunkIdx];
    if (!chunk.trim()) {
      this.moveToNextChunk(gen);
      return;
    }

    // 预计算当前 chunk 的句子列表（用于跟读高亮推进）
    this.chunkSentences = this.buildChunkSentences(chunk);
    this.currentSentence = '';

    const utterance = new SpeechSynthesisUtterance(chunk);
    utterance.lang = this.config.lang;
    utterance.rate = this.config.rate;
    utterance.pitch = this.config.pitch;
    utterance.volume = this.config.volume;

    const voice = this.findVoice();
    if (voice) utterance.voice = voice;

    this.state.currentChunkIndex = this.currentChunkIdx;
    this.state.progress = this.calcProgress();
    this.lastActivityTime = Date.now();

    utterance.onstart = () => {
      if (gen !== this.speakGeneration) return;
      this.lastActivityTime = Date.now();
      if (this.retryCount > 0) {
        console.log(`[TTS] chunk ${this.currentChunkIdx} 重试 ${this.retryCount} 次后成功`);
        this.retryCount = 0;
      }
      // 开始朗读：高亮首句
      this.highlightSentenceAt(0);
    };

    // 边界事件：推进跟读高亮，同时确认 TTS 引擎正常工作
    utterance.onboundary = (e) => {
      if (gen !== this.speakGeneration) return;
      this.lastActivityTime = Date.now();
      // 根据 charIndex 反查所属句子并推进高亮
      const charIndex = typeof e.charIndex === 'number' ? e.charIndex : 0;
      this.highlightSentenceAt(charIndex);
      this.emit({
        type: 'boundary',
        data: { chunk: this.currentChunkIdx, progress: this.state.progress },
      });
    };

    // 朗读完成 → 下一块
    utterance.onend = () => {
      if (gen !== this.speakGeneration) return;
      console.log(`[TTS] chunk ${this.currentChunkIdx} 完成`);
      this.retryCount = 0;
      this.moveToNextChunk(gen);
    };

    utterance.onerror = (e) => {
      if (gen !== this.speakGeneration) return;
      console.warn(`[TTS] chunk ${this.currentChunkIdx} 错误:`, e.error, `重试=${this.retryCount}`);
      if (this.stopFlag) return;

      if (e.error === 'canceled' || e.error === 'interrupted') {
        // 被浏览器中断
        if (this.retryCount < this.maxRetries) {
          // 重试当前 chunk（不跳过）
          this.retryCount++;
          setTimeout(() => this.playCurrentChunk(gen), 100);
        } else {
          // 重试次数用尽，跳到下一块
          console.warn(`[TTS] chunk ${this.currentChunkIdx} 重试 ${this.maxRetries} 次仍失败，跳过`);
          this.retryCount = 0;
          this.moveToNextChunk(gen);
        }
        return;
      }

      // 其他错误：重试
      if (this.retryCount < this.maxRetries) {
        this.retryCount++;
        setTimeout(() => this.playCurrentChunk(gen), 100);
      } else {
        this.retryCount = 0;
        this.moveToNextChunk(gen);
      }
    };

    try {
      this.synth!.speak(utterance);
    } catch (e) {
      console.error('[TTS] speak() 调用失败:', e);
      if (this.retryCount < this.maxRetries) {
        this.retryCount++;
        setTimeout(() => this.playCurrentChunk(gen), 200);
      } else {
        this.moveToNextChunk(gen);
      }
    }
  }

  private moveToNextChunk(gen: number): void {
    if (this.stopFlag || gen !== this.speakGeneration) return;

    this.currentChunkIdx++;
    this.retryCount = 0;

    if (this.currentChunkIdx >= this.chunks.length) {
      this.state.isSpeaking = false;
      this.state.progress = 100;
      this.stopWatchdog();
      this.resolveSpeak();
      this.emit({ type: 'end', data: { completed: true } });
    } else {
      setTimeout(() => {
        if (!this.stopFlag && gen === this.speakGeneration) this.playCurrentChunk(gen);
      }, 80);
    }
  }

  // ========== 看门狗：检测 TTS 引擎卡死 ==========
  //
  // 某些浏览器（特别是 Android 各种内置浏览器）的 TTS 引擎
  // 可能在不触发 onend/onerror 的情况下静默停止。
  // 看门狗通过检测 onboundary/onstart 事件来判断引擎是否正常工作。
  // 如果超过阈值无活动，认为引擎卡死，重新播放当前 chunk。

  private startWatchdog(): void {
    this.stopWatchdog();
    this.lastActivityTime = Date.now();

    this.watchdogTimer = window.setInterval(() => {
      if (!this.state.isSpeaking || this.state.isPaused || this.stopFlag) {
        this.stopWatchdog();
        return;
      }

      const elapsed = Date.now() - this.lastActivityTime;
      if (elapsed > this.watchdogInterval) {
        console.warn(`[TTS] 看门狗: ${elapsed}ms 无活动，尝试恢复 chunk ${this.currentChunkIdx}`);
        this.lastActivityTime = Date.now();

        // 先取消当前可能卡死的 utterance
        try {
          this.synth!.cancel();
        } catch (e) { /* 忽略 */ }

        // 重新播放当前 chunk
        setTimeout(() => {
          if (!this.stopFlag) {
            this.retryCount = 0;
            this.playCurrentChunk(this.speakGeneration);
          }
        }, 200);
      }
    }, this.watchdogInterval);

    console.log(`[TTS] 看门狗已启动，间隔 ${this.watchdogInterval}ms`);
  }

  private stopWatchdog(): void {
    if (this.watchdogTimer !== null) {
      clearInterval(this.watchdogTimer);
      this.watchdogTimer = null;
    }
  }

  // ========== 分块逻辑 ==========
  //
  // 策略：按句子分割，每个 chunk 包含1-2个完整句子。
  // 这样每个 chunk 的朗读时间约 3-8 秒，远低于浏览器自动暂停阈值。
  // 不依赖任何特定浏览器的 hack，通用性最好。

  private splitIntoChunks(text: string): string[] {
    const chunks: string[] = [];
    const paragraphs = text.split(/\n+/);

    for (const para of paragraphs) {
      if (!para.trim()) continue;

      // 短段落直接作为一个 chunk
      if (para.length <= this.config.chunkSize) {
        chunks.push(para);
        continue;
      }

      // 长段落：按句子分割，每个 chunk 尽量包含完整句子
      const sentences = this.splitIntoSentences(para);
      let currentChunk = '';

      for (const sentence of sentences) {
        if (currentChunk.length + sentence.length <= this.config.chunkSize) {
          currentChunk += sentence;
        } else {
          if (currentChunk) chunks.push(currentChunk);
          // 单句超长则强制截断
          if (sentence.length > this.config.chunkSize) {
            let remaining = sentence;
            while (remaining.length > 0) {
              const cutLen = Math.min(remaining.length, this.config.chunkSize);
              chunks.push(remaining.substring(0, cutLen));
              remaining = remaining.substring(cutLen);
            }
            currentChunk = '';
          } else {
            currentChunk = sentence;
          }
        }
      }
      if (currentChunk) chunks.push(currentChunk);
    }

    return chunks.filter(c => c.trim().length > 0);
  }

  // 将段落分割成句子
  private splitIntoSentences(text: string): string[] {
    const sentences: string[] = [];
    // 按中文/英文句子结束符分割，保留分隔符
    const regex = /([。！？；.!?;]+)/g;
    let lastIndex = 0;
    let match;

    while ((match = regex.exec(text)) !== null) {
      sentences.push(text.substring(lastIndex, match.index + match[1].length));
      lastIndex = match.index + match[1].length;
    }

    // 剩余部分（没有句号结尾的最后一句）
    if (lastIndex < text.length) {
      sentences.push(text.substring(lastIndex));
    }

    return sentences.length > 0 ? sentences : [text];
  }

  // 将 chunk 拆分为句子列表，记录每句在 chunk 内的起始偏移
  private buildChunkSentences(chunk: string): { text: string; start: number }[] {
    const sentences = this.splitIntoSentences(chunk);
    const result: { text: string; start: number }[] = [];
    let offset = 0;
    for (const s of sentences) {
      if (!s.trim()) {
        offset += s.length;
        continue;
      }
      result.push({ text: s, start: offset });
      offset += s.length;
    }
    return result;
  }

  // 根据 charIndex 找到所属句子并发出高亮事件；句子未变化则不发
  private highlightSentenceAt(charIndex: number): void {
    if (this.chunkSentences.length === 0) return;

    let target: string | null = null;
    // charIndex 落在某句的区间内 → 高亮该句
    for (let i = 0; i < this.chunkSentences.length; i++) {
      const cur = this.chunkSentences[i];
      const next = this.chunkSentences[i + 1];
      if (charIndex >= cur.start && (next ? charIndex < next.start : true)) {
        target = cur.text;
        break;
      }
    }
    // charIndex 落在句子间隙（如换行/标点间隙）→ 取最近的前一句
    if (!target && this.chunkSentences.length > 0) {
      target = this.chunkSentences[this.chunkSentences.length - 1].text;
    }

    if (target !== null && target !== this.currentSentence) {
      this.currentSentence = target;
      console.log(`[TTS] 跟读高亮 -> "${target}"`);
      this.emit({
        type: 'boundary',
        data: {
          chunk: this.currentChunkIdx,
          progress: this.state.progress,
          sentence: target,
          charIndex,
        },
      });
    }
  }
}

// ========== 单例 ==========

let instance: TTSService | null = null;

export function getTTSService(config?: Partial<TTSConfig>): TTSService {
  if (!instance) instance = new TTSService(config);
  return instance;
}

export function resetTTSService(): void {
  if (instance) { instance.stop(); instance = null; }
}
